import { useEffect, useState, type ReactNode } from 'react'
import { useFirstCompany, useProfile } from '@/api/profile'
import { Bot, Check, FileText, Laptop, Mail, MapPin, MessageSquare, Phone, Play, ShieldCheck, Sparkles, UserRound, Volume2, X } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'

/**
 * Aperçus de la mise en service : toute la moitié droite de l'écran montre ce
 * que le client obtient, au nom de SON entreprise (l'appelante, Sophie Lavoie,
 * est un exemple). Un seul élément principal, grand, qui sort par le bord droit ;
 * peu de mots, le reste en barres grises. Ils changent avec ses choix.
 */

export const DEGRADE_IA = 'linear-gradient(135deg, #ffb35a 0%, #ff5f6d 45%, #7b3fe4 100%)'

export type Lng = 'fr' | 'en'

const TXT = {
  fr: {
    entreprise: 'Votre entreprise',
    appelEntrant: 'Appel entrant',
    ceQuIlsEntendent: 'Ce que vos clients entendent',
    accueil:
      'Nous ne pouvons pas répondre pour le moment. Laissez votre nom, votre numéro et la raison de votre appel après le bip, et nous vous rappellerons.',
    voix: { fr: 'Voix française (Canada)', en: 'Voix anglaise' },
    courrielSujet: 'Nouveau message vocal de Sophie Lavoie',
    courrielDe: 'Callizy · à l’instant',
    recep: 'Réceptionniste IA',
    repondPour: 'répond pour {e}',
    enAppel: 'En appel',
    ia1: '{e}, bonjour ! Comment puis-je vous aider ?',
    ia2: 'Avec plaisir. Quel est votre nom et votre numéro ?',
    cl2: 'Sophie Lavoie, au 514 555-0188.',
    envoye: 'Message envoyé sur votre cellulaire',
    vocal: 'Message vocal',
    vocalTexte: 'Bonjour, c’est Sophie Lavoie. Rappelez-moi au 514 555-0188.',
    recu: 'Dans votre boîte de réception et par courriel',
    appelTermine: 'Appel entrant · 4 min 12 s',
    enregistre: 'Enregistré',
    resume: 'Résumé',
    fiche: 'Fiche client complétée',
    ficheEntreprise: 'Entreprise',
    ficheAdresse: 'Adresse',
    vide: 'Sans ces options : un numéro, une durée, et rien d’autre.',
    vocalTranscrit: 'Message vocal transcrit',
    transmise: 'Transmise aux secours',
    aucune: 'Aucune adresse à déclarer',
    equipe: 'Votre équipe',
    membres: (n: number) => (n > 1 ? `${n} membres` : '1 membre'),
    vous: 'Vous · Gestionnaire',
    invitationPrete: 'À inviter',
    retirer: 'Retirer',
    unCollegue: 'Un collègue',
    sonneChez: (n: number) => (n > 1 ? `Sonne chez ${n} personnes` : 'Sonne chez vous'),
    votreNumero: 'Votre numéro Callizy',
    scannez: 'Scannez avec votre téléphone',
    scannezDetail: 'iPhone et Android',
    pourMac: 'Callizy pour Mac',
    pourMacDetail: 'Vos appels sur l’ordinateur, même le navigateur fermé',
    actif: 'Actif',
    activation: 'Activation…',
    appels: 'Appels',
    textos: 'Textos',
    messagerieVocale: 'Messagerie vocale',
    enVerification: 'en vérification',
    aActiver: 'à activer',
    local: 'Numéro local',
    sansFrais: 'Sans frais',
  },
  en: {
    entreprise: 'Your company',
    appelEntrant: 'Incoming call',
    ceQuIlsEntendent: 'What your callers hear',
    accueil:
      'We can’t take your call right now. Please leave your name, number and the reason for your call after the beep, and we’ll call you back.',
    voix: { fr: 'French voice (Canada)', en: 'English voice' },
    courrielSujet: 'New voicemail from Sophie Lavoie',
    courrielDe: 'Callizy · just now',
    recep: 'AI receptionist',
    repondPour: 'answering for {e}',
    enAppel: 'On call',
    ia1: '{e}, hello! How can I help you?',
    ia2: 'Happy to help. May I have your name and number?',
    cl2: 'Sophie Lavoie, 514 555-0188.',
    envoye: 'Message sent to your cell phone',
    vocal: 'Voicemail',
    vocalTexte: 'Hi, it’s Sophie Lavoie. Call me back at 514 555-0188.',
    recu: 'In your inbox and by email',
    appelTermine: 'Incoming call · 4 min 12 s',
    enregistre: 'Recorded',
    resume: 'Summary',
    fiche: 'Contact details filled in',
    ficheEntreprise: 'Company',
    ficheAdresse: 'Address',
    vide: 'Without these options: a number, a duration, nothing else.',
    vocalTranscrit: 'Voicemail transcribed',
    transmise: 'Sent to emergency services',
    aucune: 'No address to register',
    equipe: 'Your team',
    membres: (n: number) => (n > 1 ? `${n} members` : '1 member'),
    vous: 'You · Manager',
    invitationPrete: 'To invite',
    retirer: 'Remove',
    unCollegue: 'A teammate',
    sonneChez: (n: number) => (n > 1 ? `Rings ${n} people` : 'Rings you'),
    votreNumero: 'Your Callizy number',
    scannez: 'Scan with your phone',
    scannezDetail: 'iPhone and Android',
    pourMac: 'Callizy for Mac',
    pourMacDetail: 'Your calls on the computer, even with the browser closed',
    actif: 'Active',
    activation: 'Activating…',
    appels: 'Calls',
    textos: 'Texts',
    messagerieVocale: 'Voicemail',
    enVerification: 'in review',
    aActiver: 'to activate',
    local: 'Local number',
    sansFrais: 'Toll-free',
  },
} as const

const t = (lng: string) => TXT[lng === 'en' ? 'en' : 'fr']

/**
 * L'appel montré en exemple, selon le secteur choisi à l'étape 2 : chacun voit
 * un appel de son métier, pas le même pour tous. « other », un secteur vide
 * (inscriptions d'avant le champ obligatoire) ou inconnu : l'exemple neutre.
 */
type Exemple = { dit: string; resume: string; aFaire: string }

const AUTRE: Record<Lng, Exemple> = {
  fr: {
    dit: 'J’aimerais prendre rendez-vous cette semaine.',
    resume: 'Veut un rendez-vous cette semaine, plutôt le matin.',
    aFaire: 'À faire : rappeler pour confirmer.',
  },
  en: {
    dit: 'I’d like to book an appointment this week.',
    resume: 'Wants an appointment this week, mornings preferred.',
    aFaire: 'To do: call back to confirm.',
  },
}

const EXEMPLES: Record<Lng, Record<string, Exemple>> = {
  fr: {
    technology: {
      dit: 'Notre réseau ne fonctionne plus depuis ce matin.',
      resume: 'Réseau du bureau en panne, 8 postes touchés.',
      aFaire: 'À faire : rappeler avant midi.',
    },
    marketing: {
      dit: 'Je cherche une agence pour l’ouverture de notre boutique.',
      resume: 'Ouverture en mars, budget à discuter.',
      aFaire: 'À faire : envoyer une proposition avant jeudi.',
    },
    healthcare: {
      dit: 'J’aimerais prendre un premier rendez-vous.',
      resume: 'Nouvelle patiente, préfère le matin.',
      aFaire: 'À faire : confirmer jeudi 9 h.',
    },
    realestate: {
      dit: 'Je voudrais visiter la maison de la rue Principale.',
      resume: 'Veut visiter samedi, financement approuvé.',
      aFaire: 'À faire : confirmer la visite de samedi.',
    },
    finance: {
      dit: 'J’aimerais faire le point sur mes placements.',
      resume: 'Veut revoir ses placements avant la fin de l’année.',
      aFaire: 'À faire : fixer un rendez-vous la semaine prochaine.',
    },
    retail: {
      dit: 'Avez-vous encore le manteau bleu en taille M ?',
      resume: 'Cherche le manteau bleu en M, passe samedi.',
      aFaire: 'À faire : mettre le manteau de côté.',
    },
    education: {
      dit: 'J’aimerais inscrire mon fils à la session d’hiver.',
      resume: 'Inscription de son fils de 12 ans.',
      aFaire: 'À faire : envoyer le formulaire d’inscription.',
    },
    construction: {
      dit: 'J’aimerais une soumission pour des travaux.',
      resume: 'Rénovation d’un sous-sol, visite possible jeudi.',
      aFaire: 'À faire : envoyer la soumission avant jeudi.',
    },
    legal: {
      dit: 'J’aurais besoin de conseils pour un contrat.',
      resume: 'Bail commercial à revoir avant le 15.',
      aFaire: 'À faire : fixer une consultation.',
    },
    hospitality: {
      dit: 'Avez-vous une chambre pour deux le week-end prochain ?',
      resume: 'Chambre pour 2, du 14 au 16, arrivée tardive.',
      aFaire: 'À faire : envoyer la confirmation.',
    },
    consulting: {
      dit: 'J’aimerais discuter d’un projet avec vous.',
      resume: 'Projet à lancer au printemps, veut un premier appel.',
      aFaire: 'À faire : fixer un appel cette semaine.',
    },
    transport_logistics: {
      dit: 'J’aimerais un prix pour livrer 3 palettes à Québec.',
      resume: '3 palettes de Montréal à Québec, pour mardi.',
      aFaire: 'À faire : envoyer le prix avant midi.',
    },
    trades: {
      dit: 'J’aimerais une soumission. Pouvez-vous passer cette semaine ?',
      resume: 'Veut une soumission, disponible jeudi pour une visite.',
      aFaire: 'À faire : envoyer la soumission avant jeudi.',
    },
  },
  en: {
    technology: {
      dit: 'Our network has been down since this morning.',
      resume: 'Office network down, 8 workstations affected.',
      aFaire: 'To do: call back before noon.',
    },
    marketing: {
      dit: 'I’m looking for an agency for our store opening.',
      resume: 'Opening in March, budget to discuss.',
      aFaire: 'To do: send a proposal before Thursday.',
    },
    healthcare: {
      dit: 'I’d like to book a first appointment.',
      resume: 'New patient, prefers mornings.',
      aFaire: 'To do: confirm Thursday at 9 a.m.',
    },
    realestate: {
      dit: 'I’d like to visit the house on Main Street.',
      resume: 'Wants to visit Saturday, financing approved.',
      aFaire: 'To do: confirm Saturday’s visit.',
    },
    finance: {
      dit: 'I’d like to review my investments.',
      resume: 'Wants to review her investments before year end.',
      aFaire: 'To do: book a meeting next week.',
    },
    retail: {
      dit: 'Do you still have the blue coat in medium?',
      resume: 'Looking for the blue coat in medium, coming in Saturday.',
      aFaire: 'To do: set the coat aside.',
    },
    education: {
      dit: 'I’d like to register my son for the winter session.',
      resume: 'Registering her 12-year-old son.',
      aFaire: 'To do: send the registration form.',
    },
    construction: {
      dit: 'I’d like a quote for some work.',
      resume: 'Basement renovation, visit possible Thursday.',
      aFaire: 'To do: send the quote before Thursday.',
    },
    legal: {
      dit: 'I need advice on a contract.',
      resume: 'Commercial lease to review before the 15th.',
      aFaire: 'To do: book a consultation.',
    },
    hospitality: {
      dit: 'Do you have a room for two next weekend?',
      resume: 'Room for 2, the 14th to the 16th, late arrival.',
      aFaire: 'To do: send the confirmation.',
    },
    consulting: {
      dit: 'I’d like to discuss a project with you.',
      resume: 'Project starting in the spring, wants a first call.',
      aFaire: 'To do: book a call this week.',
    },
    transport_logistics: {
      dit: 'I’d like a price to ship 3 pallets to Quebec City.',
      resume: '3 pallets from Montreal to Quebec City, for Tuesday.',
      aFaire: 'To do: send the price before noon.',
    },
    trades: {
      dit: 'I’d like a quote. Can you come by this week?',
      resume: 'Wants a quote, available Thursday for a visit.',
      aFaire: 'To do: send the quote before Thursday.',
    },
  },
}

/** Secteurs où l'appelante appelle pour une entreprise : la fiche montre son entreprise, sinon son adresse. */
const APPEL_D_ENTREPRISE = new Set(['technology', 'marketing', 'legal', 'consulting', 'transport_logistics'])

/**
 * Le secteur de l'entreprise du client. `undefined` pendant le chargement :
 * l'exemple reste en barres grises plutôt que de changer sous les yeux.
 */
function useSecteur(): string | undefined {
  const { data, isPending, isError } = useFirstCompany()
  if (isPending && !isError) return undefined
  return data?.industry ?? ''
}

const exemple = (lng: string, secteur: string): Exemple => {
  const l: Lng = lng === 'en' ? 'en' : 'fr'
  return EXEMPLES[l][secteur] ?? AUTRE[l]
}

/**
 * Le nom de l'entreprise du client (obligatoire à l'étape 2, donc toujours
 * là après le paiement). Pendant le chargement : une barre grise, jamais un
 * texte provisoire qui change sous les yeux. « Votre entreprise » seulement
 * si la lecture échoue.
 */
function useEntreprise(lng: string): ReactNode {
  const { data, isPending, isError } = useFirstCompany()
  const nom = data?.name?.trim()
  if (nom) return nom
  if (isPending && !isError) {
    return <span className="inline-block align-middle w-24 h-3 rounded bg-white/10" aria-hidden />
  }
  return t(lng).entreprise
}

/** Place le nom de l'entreprise à la place de {e}. */
function avecNom(texte: string, nom: ReactNode): ReactNode {
  const [avant, apres] = texte.split('{e}')
  return apres === undefined ? (
    texte
  ) : (
    <>
      {avant}
      {nom}
      {apres}
    </>
  )
}

// ---------------------------------------------------------------------------
// Briques
// ---------------------------------------------------------------------------

// Seul mouvement : ce que la vraie app fait pendant un appel (la voix qui parle).
const ANIMATIONS = `
@keyframes cz-voix { 0% { transform: scale(1); opacity: 0.55 } 100% { transform: scale(1.55); opacity: 0 } }
.cz-voix { animation: cz-voix 1.6s ease-out infinite }
@media (prefers-reduced-motion: reduce) { .cz-voix { animation: none; opacity: 0 } }
`

const FOND_CARTE = { backgroundColor: '#12223D', border: '1px solid rgba(255,255,255,0.09)' }

/**
 * La scène de la moitié droite : une lueur au bas (couleurs IA ou neutre) et
 * une colonne qui part à 14 % du bord gauche et sort de l'écran à droite.
 */
function Scene({ ia = false, children }: { ia?: boolean; children: ReactNode }) {
  return (
    <div className="relative h-full">
      <style>{ANIMATIONS}</style>
      <div
        className="pointer-events-none absolute -bottom-48 left-0 right-0 h-96 rounded-full blur-3xl"
        style={{
          background: ia
            ? 'radial-gradient(closest-side, rgba(255,95,109,0.20), rgba(123,63,228,0.14) 55%, transparent)'
            : 'radial-gradient(closest-side, rgba(148,163,184,0.10), transparent)',
        }}
      />
      <div className="absolute top-1/2 -translate-y-1/2 left-[14%] w-[700px] flex flex-col gap-5">{children}</div>
    </div>
  )
}

/** L'élément principal : grand, son bord droit coupé par l'écran. */
function GrandeCarte({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-[28px] p-7 pr-28 ${className}`} style={{ ...FOND_CARTE, boxShadow: '0 40px 80px -40px rgba(0,0,0,0.9)' }}>
      {children}
    </div>
  )
}

/** Le détail secondaire, décalé sous la grande carte. */
function PetiteCarte({ icone, titre, children }: { icone: ReactNode; titre: ReactNode; children?: ReactNode }) {
  return (
    <div
      className="ml-16 w-[430px] rounded-2xl p-5 flex items-center gap-4"
      style={{ backgroundColor: '#152743', border: '1px solid rgba(255,255,255,0.09)', boxShadow: '0 30px 60px -30px rgba(0,0,0,0.9)' }}
    >
      <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(52,211,153,0.15)' }}>
        {icone}
      </span>
      <div className="min-w-0 flex-grow">
        <div className="text-[15px] font-semibold">{titre}</div>
        <div className="mt-2">{children ?? <Barre largeur="75%" />}</div>
      </div>
    </div>
  )
}

/** Une ligne de texte suggérée : on comprend qu'il y a du texte sans avoir à le lire. */
function Barre({ largeur, clair = false }: { largeur: string; clair?: boolean }) {
  return (
    <span
      className="block h-2.5 rounded-full"
      style={{ width: largeur, backgroundColor: clair ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.12)' }}
    />
  )
}

function EnTete({ icone, titre, sousTitre, droite }: { icone: ReactNode; titre: ReactNode; sousTitre: ReactNode; droite?: ReactNode }) {
  return (
    <div className="flex items-center gap-4 mb-7">
      {icone}
      <div className="flex-grow min-w-0">
        <div className="text-[18px] font-semibold">{titre}</div>
        <div className="text-[14px]" style={{ color: '#8B9BB4' }}>
          {sousTitre}
        </div>
      </div>
      {droite}
    </div>
  )
}

function Rond({ children, couleur = 'rgba(255,255,255,0.1)' }: { children: ReactNode; couleur?: string }) {
  return (
    <span className="w-12 h-12 rounded-full flex items-center justify-center text-[15px] font-semibold flex-shrink-0" style={{ backgroundColor: couleur }}>
      {children}
    </span>
  )
}

function IconeIa({ taille = 36, parle = false }: { taille?: number; parle?: boolean }) {
  if (parle) {
    return (
      <span className="relative flex-shrink-0" style={{ width: taille, height: taille }}>
        <span className="cz-voix absolute inset-0 rounded-full" style={{ background: DEGRADE_IA }} />
        <IconeIa taille={taille} />
      </span>
    )
  }
  return (
    <span
      className="relative rounded-full flex items-center justify-center flex-shrink-0 text-white"
      style={{ width: taille, height: taille, background: DEGRADE_IA }}
    >
      <Bot className="w-5 h-5" />
    </span>
  )
}

const ONDE = [6, 12, 18, 9, 22, 14, 26, 11, 17, 24, 8, 19, 13, 21, 7, 16, 23, 10, 15, 20, 9, 14, 18, 6, 12, 17, 22, 11, 8, 13]

function Lecteur({ duree, libelle }: { duree?: string; libelle?: string }) {
  return (
    <div className="flex items-center gap-4">
      <span className="w-11 h-11 rounded-full bg-white flex items-center justify-center flex-shrink-0">
        <Play className="w-4 h-4 ml-0.5" style={{ color: '#0A192F' }} fill="#0A192F" />
      </span>
      <div className="flex items-center gap-[4px] h-10 flex-grow">
        {ONDE.map((h, i) => (
          <span
            key={i}
            className="w-[4px] rounded-full"
            style={{ height: h * 1.4, backgroundColor: i < 11 ? '#E2E8F0' : 'rgba(255,255,255,0.18)' }}
          />
        ))}
      </div>
      {duree && (
        <span className="text-[14px] tabular-nums" style={{ color: '#8B9BB4' }}>
          {duree}
        </span>
      )}
      {libelle && (
        <span className="text-[12px] font-medium px-2.5 py-1 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.07)', color: '#CBD5E1' }}>
          {libelle}
        </span>
      )}
    </div>
  )
}

/** La durée de l'appel qui avance, comme dans l'app. */
function Chrono({ depart }: { depart: number }) {
  const [s, setS] = useState(depart)
  useEffect(() => {
    const id = setInterval(() => setS((x) => x + 1), 1000)
    return () => clearInterval(id)
  }, [])
  return (
    <span className="tabular-nums">
      {Math.floor(s / 60)}:{String(s % 60).padStart(2, '0')}
    </span>
  )
}

const FOND_BULLE_IA = { background: 'linear-gradient(135deg, rgba(255,179,90,0.16), rgba(123,63,228,0.22))', border: '1px solid rgba(255,140,120,0.18)' }

function Bulle({ ia, children }: { ia?: boolean; children: ReactNode }) {
  return (
    <div className={`flex ${ia ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[80%] px-5 py-3.5 text-[17px] leading-snug ${ia ? 'rounded-3xl rounded-bl-lg' : 'rounded-3xl rounded-br-lg'}`}
        style={ia ? FOND_BULLE_IA : { backgroundColor: 'rgba(255,255,255,0.08)' }}
      >
        {children}
      </div>
    </div>
  )
}

function BulleGrise({ ia, largeurs }: { ia?: boolean; largeurs: string[] }) {
  return (
    <div className={`flex ${ia ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`w-[62%] px-5 py-4 space-y-2.5 ${ia ? 'rounded-3xl rounded-bl-lg' : 'rounded-3xl rounded-br-lg'}`}
        style={
          ia
            ? { background: 'linear-gradient(135deg, rgba(255,179,90,0.12), rgba(123,63,228,0.16))', border: '1px solid rgba(255,140,120,0.14)' }
            : { backgroundColor: 'rgba(255,255,255,0.06)' }
        }
      >
        {largeurs.map((l, i) => (
          <Barre key={i} largeur={l} clair={ia} />
        ))}
      </div>
    </div>
  )
}

/** La première phrase d'un texte, pour n'en montrer que l'essentiel. */
const premierePhrase = (texte: string) => texte.split(/(?<=[.!?])\s/)[0]

const initiales = (texte: string) =>
  texte
    .split(/[\s@._-]+/)
    .filter((m) => /^\p{L}/u.test(m))
    .slice(0, 2)
    .map((m) => m[0]?.toUpperCase())
    .join('')

// ---------------------------------------------------------------------------
// 2. Langue : l'accueil entendu par les clients, dans la langue principale
// ---------------------------------------------------------------------------

export function ApercuLangue({ locale, langue }: { locale: string; langue: Lng }) {
  // Libellés et courriel : la langue de l'interface. L'accueil entendu : la langue principale.
  const L = t(locale)
  const P = t(langue)
  const nom = useEntreprise(locale)
  return (
    <Scene>
      <GrandeCarte>
        <EnTete
          icone={
            <Rond couleur="rgba(52,211,153,0.15)">
              <Phone className="w-5 h-5" style={{ color: '#4ADE80' }} />
            </Rond>
          }
          titre={L.appelEntrant}
          sousTitre={nom}
        />
        <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.08em] mb-3" style={{ color: '#64748B' }}>
          <Volume2 className="w-4 h-4" /> {L.ceQuIlsEntendent}
        </div>
        <p lang={langue} className="text-[20px] leading-snug">
          « {premierePhrase(P.accueil)} »
        </p>
        <div className="mt-4 space-y-2.5">
          <Barre largeur="86%" />
          <Barre largeur="48%" />
        </div>
        <div className="mt-5 text-[14px]" style={{ color: '#8B9BB4' }}>
          {L.voix[langue]}
        </div>
      </GrandeCarte>
      <PetiteCarte icone={<Mail className="w-5 h-5" style={{ color: '#4ADE80' }} />} titre={L.courrielSujet} />
    </Scene>
  )
}

// ---------------------------------------------------------------------------
// 3. Qui répond : la réceptionniste en appel, ou le message vocal
// ---------------------------------------------------------------------------

export function ApercuReponse({ locale, langue, ia }: { locale: string; langue: Lng; ia: boolean }) {
  // Ce que disent l'appelant et la réceptionniste : la langue principale.
  // Ce que le client lit (libellés) : sa langue d'interface.
  const L = t(locale)
  const P = t(langue)
  const nom = useEntreprise(locale)
  const secteur = useSecteur()
  return (
    <Scene ia={ia}>
      <GrandeCarte>
        {ia ? (
          <>
            <EnTete
              icone={<IconeIa taille={48} parle />}
              titre={L.recep}
              sousTitre={avecNom(L.repondPour, nom)}
              droite={
                <span className="flex items-center gap-2 text-[14px] font-medium" style={{ color: '#4ADE80' }}>
                  <span className="w-2 h-2 rounded-full bg-[#4ADE80] animate-pulse" /> {L.enAppel} · <Chrono depart={42} />
                </span>
              }
            />
            <div className="space-y-3">
              <Bulle ia>{avecNom(P.ia1, nom)}</Bulle>
              {secteur === undefined ? <BulleGrise largeurs={['70%']} /> : <Bulle>{exemple(langue, secteur).dit}</Bulle>}
              <BulleGrise ia largeurs={['88%', '54%']} />
              <BulleGrise largeurs={['70%']} />
            </div>
          </>
        ) : (
          <>
            <EnTete icone={<Rond>SL</Rond>} titre="Sophie Lavoie" sousTitre={`${L.vocal} · 0:23`} />
            <Lecteur />
            <div className="mt-7 space-y-3">
              <p className="text-[17px] leading-snug">« {premierePhrase(P.vocalTexte)} »</p>
              <Barre largeur="84%" />
              <Barre largeur="52%" />
            </div>
          </>
        )}
      </GrandeCarte>
      {ia ? (
        <PetiteCarte icone={<MessageSquare className="w-5 h-5" style={{ color: '#4ADE80' }} />} titre={L.envoye} />
      ) : (
        <PetiteCarte icone={<Check className="w-5 h-5" style={{ color: '#4ADE80' }} />} titre={L.recu} />
      )}
    </Scene>
  )
}

// ---------------------------------------------------------------------------
// 4. Options : l'appel terminé, avec ce que chaque option y ajoute
// ---------------------------------------------------------------------------

export function ApercuOptions({
  locale,
  langue,
  enregistrement,
  resume,
  transcription,
}: {
  locale: string
  langue: Lng
  enregistrement: boolean
  resume: boolean
  transcription: boolean
}) {
  const L = t(locale)
  const P = t(langue)
  const avecResume = resume && enregistrement && transcription
  const secteur = useSecteur()
  const ex = secteur === undefined ? null : exemple(locale, secteur)
  const parEntreprise = !!secteur && APPEL_D_ENTREPRISE.has(secteur)
  return (
    <Scene ia={avecResume}>
      <GrandeCarte>
        <EnTete icone={<Rond>SL</Rond>} titre="Sophie Lavoie" sousTitre={L.appelTermine} />
        {enregistrement && <Lecteur duree="4:12" libelle={L.enregistre} />}
        {avecResume && (
          <div className="mt-6 rounded-2xl p-5" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
            <div className="flex items-center gap-2 text-[14px] font-semibold mb-3">
              <Sparkles className="w-4 h-4" style={{ color: '#FF8A7A' }} />
              <span style={{ background: DEGRADE_IA, WebkitBackgroundClip: 'text', color: 'transparent' }}>{L.resume}</span>
            </div>
            {ex ? <p className="text-[16px] leading-snug">{ex.resume}</p> : <Barre largeur="80%" clair />}
            <div className="mt-3">
              <Barre largeur="62%" />
            </div>
            <div className="mt-4 text-[15px] font-medium" style={{ color: '#FDBA74' }}>
              {ex ? ex.aFaire : <Barre largeur="45%" />}
            </div>
          </div>
        )}
        {!enregistrement && (
          <p className="text-[16px]" style={{ color: '#8B9BB4' }}>
            {L.vide}
          </p>
        )}
        {transcription && (
          // Détail discret : les messages vocaux, eux, sont transcrits même sans enregistrement d'appel.
          <div className="mt-6 flex items-center gap-3 text-[14px]" style={{ color: '#A8B4C7' }}>
            <FileText className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              {L.vocalTranscrit} · « {premierePhrase(P.vocalTexte)} »
            </span>
          </div>
        )}
      </GrandeCarte>
      {avecResume && (
        <PetiteCarte
          icone={<UserRound className="w-5 h-5" style={{ color: '#4ADE80' }} />}
          titre={L.fiche}
        >
          <div className="flex items-center gap-2 text-[14px]">
            <span style={{ color: '#8B9BB4' }}>{parEntreprise ? L.ficheEntreprise : L.ficheAdresse}</span>
            <span className="font-medium">{parEntreprise ? 'Lavoie Design' : 'Laval'}</span>
            <span className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: DEGRADE_IA }}>
              <Sparkles className="w-2.5 h-2.5 text-white" />
            </span>
          </div>
        </PetiteCarte>
      )}
    </Scene>
  )
}

// ---------------------------------------------------------------------------
// 5. 911 : l'adresse que les secours recevront
// ---------------------------------------------------------------------------

export function ApercuUrgence({
  locale,
  ligne1,
  ligne2,
  horsAmerique,
}: {
  locale: string
  ligne1: string
  ligne2: string
  horsAmerique: boolean
}) {
  const L = t(locale)
  return (
    <Scene>
      <div className="rounded-[28px] overflow-hidden" style={{ ...FOND_CARTE, boxShadow: '0 40px 80px -40px rgba(0,0,0,0.9)' }}>
        {/* Un bout de plan dessiné : des rues, une épingle. */}
        <div
          className="relative h-72"
          style={{
            backgroundColor: '#0F1E36',
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.05) 2px, transparent 2px), linear-gradient(90deg, rgba(255,255,255,0.05) 2px, transparent 2px), linear-gradient(35deg, transparent 48%, rgba(255,255,255,0.08) 48%, rgba(255,255,255,0.08) 52%, transparent 52%)',
            backgroundSize: '56px 56px, 56px 56px, 100% 100%',
          }}
        >
          {!horsAmerique && (
            <span className="absolute left-[40%] top-1/2 -translate-x-1/2 -translate-y-full">
              <span
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#EF4444', boxShadow: '0 0 0 12px rgba(239,68,68,0.18)' }}
              >
                <MapPin className="w-7 h-7 text-white" />
              </span>
            </span>
          )}
        </div>
        <div className="p-7 pr-28">
          {horsAmerique ? (
            <div className="text-[17px]" style={{ color: '#A8B4C7' }}>
              {L.aucune}
            </div>
          ) : (
            <>
              <div className="text-[20px] font-semibold">{ligne1}</div>
              <div className="text-[15px] mt-1" style={{ color: '#8B9BB4' }}>
                {ligne2}
              </div>
              <div
                className="mt-4 inline-flex items-center gap-2 text-[14px] font-medium px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: 'rgba(52,211,153,0.12)', color: '#4ADE80' }}
              >
                <ShieldCheck className="w-4 h-4" /> {L.transmise}
              </div>
            </>
          )}
        </div>
      </div>
    </Scene>
  )
}

// ---------------------------------------------------------------------------
// 6. Équipe : la liste se remplit à chaque collègue ajouté
// ---------------------------------------------------------------------------

export function ApercuEquipe({
  locale,
  emails,
  onRetirer,
}: {
  locale: string
  emails: string[]
  onRetirer: (email: string) => void
}) {
  const L = t(locale)
  const { data: profil } = useProfile()
  const moi = [profil?.first_name, profil?.last_name].filter(Boolean).join(' ').trim()
  const total = 1 + emails.length
  return (
    <Scene>
      <GrandeCarte>
        <div className="flex items-center justify-between mb-5">
          <div className="text-[18px] font-semibold">{L.equipe}</div>
          <div className="text-[14px] tabular-nums" style={{ color: '#8B9BB4' }}>
            {L.membres(total)}
          </div>
        </div>
        <div className="divide-y divide-white/[0.06]">
          <div className="flex items-center gap-4 py-3.5">
            <Rond>{initiales(moi || 'Vous') || '•'}</Rond>
            <div className="min-w-0">
              <div className="ph-mask text-[16px] font-medium truncate">{moi || '…'}</div>
              <div className="text-[14px]" style={{ color: '#8B9BB4' }}>
                {L.vous}
              </div>
            </div>
          </div>
          {emails.map((e) => (
            // ph-no-capture : la ligne entière, car l'adresse est aussi dans aria-label,
            // que le masquage de texte ne couvre pas.
            <div key={e} className="ph-no-capture flex items-center gap-4 py-3.5">
              <Rond>{initiales(e.split('@')[0]) || '•'}</Rond>
              <div className="min-w-0 flex-grow text-[16px] font-medium truncate">{e}</div>
              <span
                className="flex-shrink-0 text-[12px] font-medium px-2.5 py-1 rounded-lg"
                style={{ backgroundColor: 'rgba(52,211,153,0.12)', color: '#4ADE80' }}
              >
                {L.invitationPrete}
              </span>
              <button
                type="button"
                onClick={() => onRetirer(e)}
                aria-label={`${L.retirer} ${e}`}
                className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
                style={{ color: '#8B9BB4' }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          {emails.length === 0 &&
            [0, 1].map((i) => (
              <div key={i} className="flex items-center gap-4 py-3.5">
                <span className="w-12 h-12 rounded-full flex-shrink-0" style={{ border: '1.5px dashed rgba(255,255,255,0.18)' }} />
                <Barre largeur="40%" />
              </div>
            ))}
        </div>
      </GrandeCarte>
      <PetiteCarte icone={<Phone className="w-5 h-5" style={{ color: '#4ADE80' }} />} titre={L.appelEntrant}>
        <div className="text-[14px]" style={{ color: '#8B9BB4' }}>
          {L.sonneChez(total)}
        </div>
      </PetiteCarte>
    </Scene>
  )
}

// ---------------------------------------------------------------------------
// 1. Bienvenue : le numéro du client, en grand
// ---------------------------------------------------------------------------

/** État des textos d'un numéro : un sans-frais doit être vérifié auprès des opérateurs. */
export type EtatTextos = 'actif' | 'verification' | 'inactif'

export function ApercuNumero({
  locale,
  numero,
  activation,
  sansFrais,
  textos,
}: {
  locale: string
  numero: string
  activation: boolean
  sansFrais: boolean
  textos: EtatTextos
}) {
  const L = t(locale)
  const pret = !!numero && !activation
  const capacites: { icone: ReactNode; libelle: string; etat: EtatTextos; precision?: string }[] = [
    { icone: <Phone className="w-4 h-4" />, libelle: L.appels, etat: 'actif' },
    {
      icone: <MessageSquare className="w-4 h-4" />,
      libelle: L.textos,
      etat: textos,
      precision: textos === 'verification' ? L.enVerification : textos === 'inactif' ? L.aActiver : undefined,
    },
    { icone: <Mail className="w-4 h-4" />, libelle: L.messagerieVocale, etat: 'actif' },
  ]
  return (
    <Scene>
      <GrandeCarte>
        <div className="flex items-center justify-between mb-5">
          <div className="text-[12px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#64748B' }}>
            {L.votreNumero}
          </div>
          <span
            className="flex items-center gap-2 text-[13px] font-medium px-2.5 py-1 rounded-lg"
            style={pret ? { backgroundColor: 'rgba(52,211,153,0.12)', color: '#4ADE80' } : { backgroundColor: 'rgba(255,255,255,0.06)', color: '#8B9BB4' }}
          >
            {pret && <span className="w-2 h-2 rounded-full bg-[#4ADE80]" />}
            {pret ? L.actif : L.activation}
          </span>
        </div>
        {pret ? (
          <>
            <div className="text-[56px] font-semibold tracking-[-0.02em] tabular-nums leading-none whitespace-nowrap">{numero}</div>
            <div className="mt-3 text-[15px]" style={{ color: '#8B9BB4' }}>
              {sansFrais ? L.sansFrais : L.local}
            </div>
          </>
        ) : (
          <span className="block h-12 w-[70%] rounded-xl bg-white/[0.08] animate-pulse" />
        )}
        <div className="mt-7 flex flex-wrap gap-3">
          {capacites.map((c) => (
            <span
              key={c.libelle}
              className="inline-flex items-center gap-2 text-[14px] font-medium px-3.5 py-2 rounded-xl"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: c.etat === 'inactif' ? '#8B9BB4' : '#E2E8F0' }}
            >
              {c.icone}
              {c.libelle}
              {c.precision ? (
                <span style={{ color: c.etat === 'verification' ? '#FBBF24' : '#8B9BB4' }}>· {c.precision}</span>
              ) : (
                <Check className="w-4 h-4" style={{ color: pret ? '#4ADE80' : '#475569' }} />
              )}
            </span>
          ))}
        </div>
      </GrandeCarte>
    </Scene>
  )
}

// ---------------------------------------------------------------------------
// 7. Applications : le code à scanner depuis l'écran de l'ordinateur
// ---------------------------------------------------------------------------

export function ApercuApp({ locale, lienQr }: { locale: string; lienQr: string }) {
  const L = t(locale)
  return (
    <Scene>
      <GrandeCarte className="flex items-center gap-8">
        <div className="p-4 bg-white rounded-3xl flex-shrink-0">
          <QRCodeSVG
            value={lienQr}
            size={200}
            bgColor="#ffffff"
            fgColor="#0A192F"
            level="M"
            imageSettings={{ src: 'https://sdk.callizy.ca/assets/favicon_callizy.png', height: 36, width: 36, excavate: true }}
          />
        </div>
        <div>
          <div className="text-[20px] font-semibold leading-snug">{L.scannez}</div>
          <div className="text-[15px] mt-1" style={{ color: '#8B9BB4' }}>
            {L.scannezDetail}
          </div>
        </div>
      </GrandeCarte>
      <PetiteCarte icone={<Laptop className="w-5 h-5" style={{ color: '#4ADE80' }} />} titre={L.pourMac}>
        <div className="text-[14px]" style={{ color: '#8B9BB4' }}>
          {L.pourMacDetail}
        </div>
      </PetiteCarte>
    </Scene>
  )
}
